/**
 * WHO the visit-note drafter is about — resolved ONCE, as a pair.
 *
 * The drafter takes two identifiers for the same person:
 *
 * - `memberId`  — the human member/policy id, shown in its header.
 * - `astranaId` — Astrana's INTERNAL numeric member id, never shown. Its only
 *   job is a server-side lookup: care-gql loads the note's `availableResults`
 *   (the labs the clinician picks from) via
 *   `VisitNoteResultsProvider.forMember(astranaId)`.
 *
 * Those two must always name the SAME person. If they don't, the labs picker
 * offers one patient's results inside another patient's note — and a picked
 * result can be written into a chart that is then signed. That is a
 * cross-patient PHI defect, not a cosmetic one, and this repo has already been
 * bitten twice by identity drift on this surface: a member can carry more than
 * one astranaId across systems (the EMPI mismatch that emptied a QA Claims
 * tab), and the EHR side panel can swap the patient under a mounted component.
 *
 * So the pair is derived here, in one pure function, from one member record —
 * never assembled from two places at the call site. Every branch returns BOTH
 * fields or NEITHER; there is no path that yields an `astranaId` from one
 * source and a `memberId` from another.
 *
 * When the id cannot be established the function returns nothing for it. The
 * library omits the field entirely when it is undefined and the picker simply
 * renders no labs — an empty picker is the safe failure, a wrong patient's
 * labs is not. Nothing here guesses, caches, or falls back to a last-known id.
 */
import { ASTRANA_NAV_PREFIX } from "../graphql/resolveEhrMember";

/** The Pathways record the profile loaded for the route it is rendering. */
export interface LoadedPathwaysMemberIdentity {
  /** Mongo ObjectId — the same value the route carries for a Pathways member. */
  _id?: string | null;
  memberPolicyId?: string | null;
  /** Numeric-as-string off the wire; care-gql types the input as `Int`. */
  astranaId?: string | null;
}

export interface VisitNoteMemberSource {
  /** `useParams().id` — the member the page is CURRENTLY routed to. */
  routeId?: string;
  /** The MEMBER_BY_ID result for `routeId`, or null while it is unresolved. */
  loadedMember?: LoadedPathwaysMemberIdentity | null;
  /** astranaId recovered from an `astrana:<id>` route (`astranaIdFromNav`). */
  astranaNavId?: string;
  /** Optional PP/EZCAP member id a host carried alongside an `astrana:` nav. */
  astranaCarriedMemberId?: string;
  /** PP member id for a `pp:` route. */
  ppMemberId?: string;
}

export interface VisitNoteMemberIdentity {
  memberId?: string;
  astranaId?: number;
}

const NONE: VisitNoteMemberIdentity = {};

/**
 * care-gql types `GenerateVisitNoteInput.astranaId` as `Int`. Anything that is
 * not a positive integer is not an astranaId, and sending it would either error
 * the whole generate call or — worse — coerce to some other member's row.
 */
function toAstranaInt(raw: string | null | undefined): number | undefined {
  if (raw == null) return undefined;
  const trimmed = String(raw).trim();
  if (trimmed === "") return undefined;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n <= 0) return undefined;
  return n;
}

function nonEmpty(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Resolves the drafter's member identity from the profile's own route + record.
 *
 * Three routes reach this profile, and each is handled as ONE branch so the
 * pair can never straddle two of them:
 *
 * 1. `astrana:<astranaId>` — a connect-only member with no Pathways record.
 *    Both ids come from that one navigation: the astranaId IS the route, and
 *    the member id is the extra the same nav state carried.
 * 2. `pp:<companyId>:<mpi>` — a provider-portal member. There is NO astranaId
 *    source for this path anywhere in this host, so none is passed. Deliberate:
 *    the empty picker is correct, and inventing an id here is the exact shape
 *    of the bug this file exists to prevent.
 * 3. Anything else — a Pathways member. Both ids come off the single loaded
 *    `PathwaysMember` record, and only when that record is the one for the
 *    CURRENT route (`_id === routeId`). While a member switch is in flight the
 *    profile can still be holding the previous member's record; passing its
 *    astranaId would hand the drafter the person the clinician just left.
 */
export function visitNoteMemberIdentity(
  source: VisitNoteMemberSource,
): VisitNoteMemberIdentity {
  const { routeId, loadedMember, astranaNavId, astranaCarriedMemberId, ppMemberId } =
    source;
  if (!routeId) return NONE;

  if (routeId.startsWith(ASTRANA_NAV_PREFIX)) {
    return {
      memberId: nonEmpty(astranaCarriedMemberId),
      astranaId: toAstranaInt(astranaNavId),
    };
  }

  if (routeId.startsWith("pp:")) {
    return { memberId: nonEmpty(ppMemberId) };
  }

  // The record must BE this route's member. No record (loading, error, not
  // found) and a stale record are the same answer: pass nothing.
  if (!loadedMember || loadedMember._id !== routeId) return NONE;

  return {
    memberId: nonEmpty(loadedMember.memberPolicyId),
    astranaId: toAstranaInt(loadedMember.astranaId),
  };
}
